import { HttpErrorResponse } from '@angular/common/http';

export type CrmErrorKind =
  | 'business'
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'rate-limit'
  | 'timeout'
  | 'offline'
  | 'server'
  | 'unexpected';

export class CrmApiError extends Error {
  constructor(
    readonly kind: CrmErrorKind,
    readonly status: number,
    message?: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message ?? crmErrorMessage(kind, retryAfterSeconds));
    this.name = 'CrmApiError';
  }
}

export function mapCrmError(error: unknown): CrmApiError {
  if (error instanceof CrmApiError) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return new CrmApiError(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'offline'
          : 'unexpected',
        error.status,
      );
    }
    if (error.status === 400) {
      const businessMessage = readBusinessMessage(error.error);
      return businessMessage
        ? new CrmApiError('business', 400, businessMessage)
        : new CrmApiError('validation', 400);
    }
    if (error.status === 401) {
      return new CrmApiError('unauthorized', 401);
    }
    if (error.status === 403) {
      return new CrmApiError('forbidden', 403);
    }
    if (error.status === 404) {
      const businessMessage = readBusinessMessage(error.error);
      return businessMessage
        ? new CrmApiError('business', 404, businessMessage)
        : new CrmApiError('not-found', 404);
    }
    if (error.status === 409) {
      return new CrmApiError('conflict', 409);
    }
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      return new CrmApiError(
        'rate-limit',
        429,
        undefined,
        Number.isFinite(retryAfter) ? retryAfter : undefined,
      );
    }
    if (error.status >= 500) {
      return new CrmApiError('server', error.status);
    }
  }

  if (error instanceof Error && error.name === 'TimeoutError') {
    return new CrmApiError('timeout', 0);
  }

  return new CrmApiError('unexpected', 0);
}

function readBusinessMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object' || !('error' in body)) {
    return null;
  }
  const message = (body as { error?: unknown }).error;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

function crmErrorMessage(
  kind: CrmErrorKind,
  retryAfterSeconds?: number,
): string {
  switch (kind) {
    case 'business':
    case 'validation':
      return 'Revisa los datos e intenta de nuevo.';
    case 'unauthorized':
      return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    case 'forbidden':
      return 'No tienes permiso para realizar esta acción.';
    case 'not-found':
      return 'El registro no existe o ya no está disponible.';
    case 'conflict':
      return 'Ya existe un registro con estos datos.';
    case 'rate-limit':
      return retryAfterSeconds
        ? `Demasiadas solicitudes. Intenta de nuevo en ${retryAfterSeconds} segundos.`
        : 'Demasiadas solicitudes. Espera un momento antes de volver a intentar.';
    case 'timeout':
      return 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.';
    case 'offline':
      return 'No hay conexión a internet. Verifica tu red e intenta de nuevo.';
    case 'server':
    case 'unexpected':
      return 'No fue posible completar la solicitud. Intenta de nuevo más tarde.';
  }
}
